import {
  faAdd,
  faArrowRight,
  faCircleChevronDown,
  faCog,
  faKey,
  faLink,
  faWarning,
  faWrench,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataTable } from "@ventumsoftware/react-toolkit";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { useApi, useFetchedResource } from "../../api/APIContext";
import { useApiCall } from "../../api/api";
import { CompanyCheckIns } from "../../components/companies/CompanyCheckIns";
import { ChildCompanyTile, ParentCompanyTile } from "../../components/companies/CompanyListTile";
import { CompanyLogo } from "../../components/companies/CompanyLogo";
import { resolveCompanyColorPalette } from "../../components/companies/companyColorPalette";
import { ProjectTile } from "../../components/companies/ProjectTile";
import { ContentContainer } from "../../components/content/ContentContainer";
import { SectionHeader } from "../../components/content/SectionHeader";
import { Badge, BadgeStyle } from "../../components/data/Badge";
import { TablePlaceholder } from "../../components/data/TablePlaceholder";
import { Button } from "../../components/form/Button";
import { useModal } from "../../components/layout/ModalProvider";
import { useRefreshEffect } from "../../components/RefreshController";
import { AddProjectModal } from "../../modals/AddProjectModal";
import { GrantUserAccessModal } from "../../modals/GrantUserAccessModal";
import { LinkCompanyModal } from "../../modals/LinkCompanyModal";

export const CompaniesDetailsPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const { companies, projects: allProjects, users } = useApi();

  const company = useMemo(() => {
    return companies?.find((c) => c.id === companyId);
  }, [companies, companyId]);

  const projects = useMemo(
    () => allProjects.filter((p) => p.companyId === companyId && !p.isArchived),
    [companyId, allProjects],
  );
  const { listUserPermissionsForCompany } = useApiCall();
  const userPermissions = useFetchedResource((companyId: string) =>
    listUserPermissionsForCompany(companyId),
  );
  useRefreshEffect(() => {
    if (companyId) {
      userPermissions.reload(companyId);
    }
  }, [companyId]);

  const enrichedUsers = useMemo(() => {
    const userIds = userPermissions.resource?.map((up) => up.userId) ?? [];
    return users
      .filter((u) => userIds.includes(u.id))
      .map((u) => {
        const permissions = userPermissions.resource?.find((up) => up.userId === u.id);
        return {
          ...u,
          isAdmin: u.isAdmin,
          isApiUser: u.isApiUser,
          explicitAccess: permissions?.hasExplicitAccess ?? false,
        };
      });
  }, [userPermissions, users]);

  const addProjectModal = useModal({
    title: "Project toevoegen",
    body: <AddProjectModal company={company} />,
  });
  const linkCompanyModal = useModal({
    title: "Bedrijf koppelen",
    body: <LinkCompanyModal company={company!} />,
  });
  const grantUserAccessModal = useModal({
    title: "Gebruiker toevoegen",
    body: company ? <GrantUserAccessModal company={company} /> : <></>,
  });

  if (!company) {
    return (
      <div>
        <div
          className={
            "bg-linear-30 from-slate-600 to-slate-500 border border-slate-400 opacity-40 animate-pulse mx-8 mt-8 rounded-xl h-40 flex items-center"
          }
        ></div>
      </div>
    );
  }

  const resolvedPalette = company.colorPalette
    ? resolveCompanyColorPalette(company.colorPalette)
    : null;
  return (
    <div className={""}>
      <ContentContainer size={"xl"}>
        <div
          className={"border mx-8 rounded-xl h-40 flex items-center px-8 mt-8"}
          style={{
            borderColor: resolvedPalette?.[400],
            background: `linear-gradient(210deg, ${resolvedPalette?.[100]}, ${resolvedPalette?.[200]})`,
          }}
        >
          <ContentContainer>
            <div className={"flex items-center space-x-8"}>
              <CompanyLogo company={company} />
              <h1
                className={"text-4xl font-bold font-display"}
                style={{ color: `${resolvedPalette?.[950] ?? "#000"}` }}
              >
                {company.name}
              </h1>
              <div className={"flex-1"}></div>
              <button
                className={
                  "font-medium h-10 px-3 text-base text-slate-700 hover:bg-[#0002] rounded-md"
                }
                type={"button"}
                onClick={() => navigate("./settings")}
              >
                <FontAwesomeIcon icon={faCog} className={"mr-2"} />
                Instellingen
              </button>
            </div>
          </ContentContainer>
        </div>
      </ContentContainer>

      <ContentContainer>
        {company.parentCompanies.length + company.childCompanies.length > 0 ? (
          <>
            <div className={"flex items-center justify-between"}>
              <SectionHeader>Gerelateerde bedrijven</SectionHeader>
              <div className={"mb-4"}>
                <Button
                  type={"secondary"}
                  size={"sm"}
                  text={"Bedrijf koppelen"}
                  icon={faLink}
                  disabled={!company}
                  onClick={() => linkCompanyModal.open()}
                />
              </div>
            </div>
            <div
              className={
                "grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 border-t -mt-2 pt-4 mb-8"
              }
            >
              {company.parentCompanies.map((c, i) => (
                <ParentCompanyTile
                  company={c}
                  childCompany={company}
                  key={i}
                  onClick={() => navigate(`/companies/${c.id}`)}
                />
              ))}
              {company.childCompanies.map((c, i) => (
                <ChildCompanyTile
                  company={c}
                  parentCompany={company}
                  key={i}
                  onClick={() => navigate(`/companies/${c.id}`)}
                />
              ))}
            </div>
          </>
        ) : (
          <></>
        )}
        <div className={"flex items-center justify-between"}>
          <SectionHeader>Projecten</SectionHeader>
          <div className={"mb-4"}>
            <Button
              type={"secondary"}
              size={"sm"}
              text={"Nieuw project"}
              icon={faAdd}
              onClick={() => addProjectModal.open()}
            />
          </div>
        </div>
        <div
          className={
            "grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 border-t -mt-2 pt-4 mb-8"
          }
        >
          {projects.map((project, i) => (
            <ProjectTile
              key={i}
              project={project}
              onClick={() => navigate(`/?company=${company.id}&project=${project.id}`)}
            />
          ))}
        </div>
        <div className={"flex items-center justify-between"}>
          <SectionHeader>Toegang en gebruikers</SectionHeader>
          <div className={"mb-4"}>
            <Button
              type={"secondary"}
              size={"sm"}
              text={"Nieuwe gebruiker"}
              icon={faAdd}
              onClick={() => grantUserAccessModal.open()}
            />
          </div>
        </div>
        <div className={"border-t -mt-2 pt-4 mb-8"}>
          {enrichedUsers.length === 0 ? (
            <TablePlaceholder>Geen directe gebruikers onder dit bedrijf</TablePlaceholder>
          ) : (
            <DataTable
              keyProperty={"id"}
              compact={true}
              data={enrichedUsers}
              pageSize={50}
              columns={[
                {
                  header: "Naam",
                  sort: true,
                  property: "last_name",
                  transform: (_, row) => (
                    <div>
                      <div>
                        {row.first_name} {row.last_name}
                      </div>
                      <div className={"text-slate-600 text-xs"}>
                        {row.email} &middot; {row.username}
                      </div>
                    </div>
                  ),
                },
                {
                  header: "Toegang",
                  sort: true,
                  property: "explicitAccess",
                  transform: (value, row) => {
                    const badges = [];
                    if (value) {
                      badges.push(
                        <Badge style={BadgeStyle.info}>
                          <FontAwesomeIcon icon={faArrowRight} className={"mr-1"} /> Directe toegang
                        </Badge>,
                      );
                    } else {
                      badges.push(
                        <Badge style={BadgeStyle.default}>
                          <FontAwesomeIcon icon={faCircleChevronDown} className={"mr-1"} />{" "}
                          Impliciete toegang
                        </Badge>,
                      );
                    }

                    if (!row.enabled) {
                      badges.push(
                        <Badge style={BadgeStyle.warning}>
                          <FontAwesomeIcon icon={faWarning} className={"mr-1"} /> Inloggen
                          uitgeschakeld
                        </Badge>,
                      );
                    }
                    if (row.isAdmin) {
                      badges.push(
                        <Badge style={BadgeStyle.success}>
                          <FontAwesomeIcon icon={faWrench} className={"mr-1"} /> Administrator
                        </Badge>,
                      );
                    }
                    if (row.isApiUser) {
                      badges.push(
                        <Badge style={BadgeStyle.info}>
                          <FontAwesomeIcon icon={faKey} className={"mr-1"} /> Systeem Koppeling
                        </Badge>,
                      );
                    }
                    return <div>{badges}</div>;
                  },
                },
              ]}
            />
          )}
          {/*{projects.map((project, i) => <ProjectTile key={i} project={project} onClick={() => navigate(`/?company=${company.id}&project=${project.id}`)} />)}*/}
        </div>

        <CompanyCheckIns company={company} projects={projects} />
      </ContentContainer>
    </div>
  );
};
